/**
 * Backfill Calculator Component
 * Similar to Excavation but for importing/placing backfill materials
 * Includes CY, material type, compaction factor, and L&E integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Layers, Package, TrendingUp, Plus, Settings, X, Check, Info, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

// Backfill Materials Database
const backfillMaterialsDatabase = [
  // Granular Materials
  { name: 'Crushed Stone (3/4")', category: 'Granular', tonsPerCY: 1.4, compactionFactor: 0.90, description: '3/4 inch crushed aggregate' },
  { name: 'Crushed Stone (1.5")', category: 'Granular', tonsPerCY: 1.4, compactionFactor: 0.90, description: '1.5 inch crushed aggregate' },
  { name: 'Crushed Gravel', category: 'Granular', tonsPerCY: 1.4, compactionFactor: 0.90, description: 'Crushed gravel backfill' },
  { name: 'Pea Gravel', category: 'Granular', tonsPerCY: 1.35, compactionFactor: 0.92, description: 'Small round gravel' },
  { name: 'Bank Run Gravel', category: 'Granular', tonsPerCY: 1.4, compactionFactor: 0.88, description: 'Natural bank run material' },
  
  // Sand Materials
  { name: 'Concrete Sand', category: 'Sand', tonsPerCY: 1.35, compactionFactor: 0.92, description: 'Washed concrete sand' },
  { name: 'Mason Sand', category: 'Sand', tonsPerCY: 1.3, compactionFactor: 0.93, description: 'Fine mason sand' },
  { name: 'Fill Sand', category: 'Sand', tonsPerCY: 1.35, compactionFactor: 0.90, description: 'General fill sand' },
  { name: 'Bedding Sand', category: 'Sand', tonsPerCY: 1.35, compactionFactor: 0.92, description: 'Pipe bedding sand' },
  
  // Base & Subbase
  { name: 'ABC Stone (Aggregate Base)', category: 'Base Materials', tonsPerCY: 1.5, compactionFactor: 0.85, description: 'Aggregate base course' },
  { name: 'Road Base', category: 'Base Materials', tonsPerCY: 1.5, compactionFactor: 0.85, description: 'Road base material' },
  { name: 'Crusher Run', category: 'Base Materials', tonsPerCY: 1.5, compactionFactor: 0.85, description: 'Crusher run aggregate' },
  { name: 'Recycled Concrete Base', category: 'Base Materials', tonsPerCY: 1.45, compactionFactor: 0.87, description: 'Recycled concrete aggregate' },
  { name: 'Recycled Asphalt (RAP)', category: 'Base Materials', tonsPerCY: 1.4, compactionFactor: 0.88, description: 'Reclaimed asphalt pavement' },
  
  // Structural Fill
  { name: 'Structural Fill', category: 'Structural', tonsPerCY: 1.5, compactionFactor: 0.85, description: 'Engineered structural fill' },
  { name: 'Select Fill', category: 'Structural', tonsPerCY: 1.45, compactionFactor: 0.87, description: 'Select quality fill material' },
  { name: 'Controlled Low Strength Material (CLSM)', category: 'Structural', tonsPerCY: 1.6, compactionFactor: 1.0, description: 'Flowable fill - no compaction needed' },
  
  // Common Fill
  { name: 'Common Fill (Dry)', category: 'Common Fill', tonsPerCY: 1.3, compactionFactor: 0.90, description: 'Dry common fill dirt' },
  { name: 'Common Fill (Moist)', category: 'Common Fill', tonsPerCY: 1.4, compactionFactor: 0.88, description: 'Optimum moisture fill' },
  { name: 'Screened Fill', category: 'Common Fill', tonsPerCY: 1.35, compactionFactor: 0.90, description: 'Screened fill material' },
  { name: 'Native Backfill', category: 'Common Fill', tonsPerCY: 1.3, compactionFactor: 0.90, description: 'Reusing excavated material' },
  
  // Specialty
  { name: 'Pipe Zone Backfill', category: 'Specialty', tonsPerCY: 1.4, compactionFactor: 0.90, description: 'Pipe zone bedding material' },
  { name: 'Flowable Fill', category: 'Specialty', tonsPerCY: 1.6, compactionFactor: 1.0, description: 'Self-leveling flowable fill' },
  { name: 'Lean Concrete Backfill', category: 'Specialty', tonsPerCY: 1.8, compactionFactor: 1.0, description: 'Low-strength concrete fill' },
  { name: 'Drainage Stone (#57)', category: 'Specialty', tonsPerCY: 1.35, compactionFactor: 0.95, description: 'Drainage layer stone' },
];

const BackfillCalculator = ({ 
  backfillItems, 
  setBackfillItems,
  taskDuration,
  setTaskDuration
}) => {
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [cubicYards, setCubicYards] = useState('');
  const [materialCost, setMaterialCost] = useState('');
  const [truckingCost, setTruckingCost] = useState('');
  const [customWeight, setCustomWeight] = useState('');
  const [customCompaction, setCustomCompaction] = useState('');

  // Custom materials management
  const [customMaterials, setCustomMaterials] = useState(() => {
    const saved = localStorage.getItem('customBackfillMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    category: 'Custom',
    tonsPerCY: '',
    compactionFactor: '0.90'
  });

  // Task Duration state
  const [localDuration, setLocalDuration] = useState(taskDuration?.value || '');
  const [durationUnit, setDurationUnit] = useState(taskDuration?.unit || 'days');

  // Save custom materials to localStorage
  useEffect(() => {
    localStorage.setItem('customBackfillMaterials', JSON.stringify(customMaterials));
  }, [customMaterials]);

  // Update parent task duration
  useEffect(() => {
    if (localDuration) {
      setTaskDuration({ value: parseFloat(localDuration), unit: durationUnit });
    }
  }, [localDuration, durationUnit, setTaskDuration]);

  // Helper to generate unique IDs
  const generateId = useCallback(() => {
    return `bf_${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Combine default and custom materials
  const allMaterials = [...backfillMaterialsDatabase, ...customMaterials];

  const addCustomMaterial = () => {
    if (!newMaterial.name.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    if (!newMaterial.tonsPerCY || parseFloat(newMaterial.tonsPerCY) <= 0) {
      toast.error('Please enter a valid weight (tons/CY)');
      return;
    }
    const material = {
      id: `custom_bf_${Date.now()}`,
      name: newMaterial.name,
      category: newMaterial.category || 'Custom',
      tonsPerCY: parseFloat(newMaterial.tonsPerCY),
      compactionFactor: parseFloat(newMaterial.compactionFactor) || 0.90,
      description: 'Custom backfill material',
      isCustom: true
    };
    setCustomMaterials([...customMaterials, material]);
    setNewMaterial({ name: '', category: 'Custom', tonsPerCY: '', compactionFactor: '0.90' });
    setShowAddForm(false);
    toast.success(`Material "${material.name}" added`);
  };

  const removeCustomMaterial = (id) => {
    setCustomMaterials(customMaterials.filter(m => m.id !== id));
    toast.success('Material removed');
  };

  const addMaterial = () => {
    if (!selectedMaterial || !cubicYards) {
      toast.error('Please select a material and enter cubic yards');
      return;
    }

    const cy = parseFloat(cubicYards);
    if (cy <= 0) {
      toast.error('Cubic yards must be greater than 0');
      return;
    }

    const materialInfo = allMaterials.find(m => m.name === selectedMaterial);
    if (!materialInfo) {
      toast.error('Invalid material selected');
      return;
    }

    // Use custom values if provided
    const effectiveWeight = parseFloat(customWeight) || materialInfo.tonsPerCY;
    const effectiveCompaction = parseFloat(customCompaction) || materialInfo.compactionFactor;

    // Calculate compacted volume and total weight
    const compactedCY = cy * effectiveCompaction;
    const totalTons = cy * effectiveWeight;
    const matCost = parseFloat(materialCost) || 0;
    const trkCost = parseFloat(truckingCost) || 0;
    const totalMaterialCost = totalTons * matCost;
    const totalTruckingCost = totalTons * trkCost;
    const totalCost = totalMaterialCost + totalTruckingCost;

    const newItem = {
      id: generateId(),
      name: materialInfo.name,
      category: materialInfo.category,
      tonsPerCY: effectiveWeight,
      originalTonsPerCY: materialInfo.tonsPerCY,
      compactionFactor: effectiveCompaction,
      originalCompaction: materialInfo.compactionFactor,
      customWeight: customWeight ? parseFloat(customWeight) : null,
      customCompaction: customCompaction ? parseFloat(customCompaction) : null,
      looseCubicYards: cy,
      compactedCubicYards: compactedCY,
      totalTons: totalTons,
      totalPounds: totalTons * 2000,
      materialCostPerTon: matCost,
      truckingCostPerTon: trkCost,
      totalMaterialCost: totalMaterialCost,
      totalTruckingCost: totalTruckingCost,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setBackfillItems([...backfillItems, newItem]);
    toast.success(`${materialInfo.name} added successfully!`);
    
    setSelectedMaterial('');
    setCubicYards('');
    setMaterialCost('');
    setTruckingCost('');
    setCustomWeight('');
    setCustomCompaction('');
  };

  const updateMaterial = (id, updatedData) => {
    setBackfillItems(backfillItems.map(material => {
      if (material.id === id) {
        const compactedCY = updatedData.looseCubicYards * material.compactionFactor;
        const totalTons = updatedData.looseCubicYards * material.tonsPerCY;
        const totalPounds = totalTons * 2000;
        return { ...material, ...updatedData, compactedCubicYards: compactedCY, totalTons, totalPounds };
      }
      return material;
    }));
    toast.success('Material updated');
  };

  const deleteMaterial = (id) => {
    const materialToDelete = backfillItems.find(m => m.id === id);
    setBackfillItems(backfillItems.filter(material => material.id !== id));
    toast.error(`${materialToDelete?.name || 'Material'} removed`);
  };

  const clearAll = () => {
    setBackfillItems([]);
    localStorage.removeItem('backfillItems');
    toast.info('All backfill materials cleared');
  };

  // Calculate totals
  const materialTotals = backfillItems.reduce((acc, material) => {
    if (!acc[material.name]) {
      acc[material.name] = {
        name: material.name,
        category: material.category,
        totalLooseCY: 0,
        totalCompactedCY: 0,
        totalTons: 0,
        count: 0
      };
    }
    acc[material.name].totalLooseCY += material.looseCubicYards;
    acc[material.name].totalCompactedCY += material.compactedCubicYards;
    acc[material.name].totalTons += material.totalTons;
    acc[material.name].count += 1;
    return acc;
  }, {});

  const grandTotal = {
    totalLooseCY: backfillItems.reduce((sum, m) => sum + m.looseCubicYards, 0),
    totalCompactedCY: backfillItems.reduce((sum, m) => sum + m.compactedCubicYards, 0),
    totalTons: backfillItems.reduce((sum, m) => sum + m.totalTons, 0),
    totalMaterialCost: backfillItems.reduce((sum, m) => sum + (m.totalMaterialCost || 0), 0),
    totalTruckingCost: backfillItems.reduce((sum, m) => sum + (m.totalTruckingCost || 0), 0),
    totalCost: backfillItems.reduce((sum, m) => sum + (m.totalCost || 0), 0),
    itemCount: backfillItems.length
  };

  const groupedMaterials = allMaterials.reduce((acc, material) => {
    const category = material.category || 'Custom';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(material);
    return acc;
  }, {});

  const selectedMaterialData = allMaterials.find(m => m.name === selectedMaterial);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="space-y-8">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Items</span>
            <Package className="w-5 h-5 text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">{grandTotal.itemCount}</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Volume (Loose)</span>
            <TrendingUp className="w-5 h-5 text-accent" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">
              {grandTotal.totalLooseCY.toFixed(1)}
              <span className="text-sm text-muted-foreground ml-1">CY</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Compacted: {grandTotal.totalCompactedCY.toFixed(1)} CY
            </p>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Weight</span>
            <Layers className="w-5 h-5 text-success" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">
              {grandTotal.totalTons.toFixed(1)}
              <span className="text-sm text-muted-foreground ml-1">tons</span>
            </p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Cost</span>
            <Layers className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(grandTotal.totalCost)}
            </p>
            <p className="text-xs text-muted-foreground">
              Mat: {formatCurrency(grandTotal.totalMaterialCost)} | Trk: {formatCurrency(grandTotal.totalTruckingCost)}
            </p>
          </div>
        </div>
      </div>

      {/* Task Duration */}
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              <Label className="font-semibold">Task Duration</Label>
            </div>
            <div className="flex items-center gap-2 flex-1 max-w-xs">
              <Input
                type="number"
                placeholder="0"
                value={localDuration}
                onChange={(e) => setLocalDuration(e.target.value)}
                min="0"
                step="0.5"
                className="w-24"
                data-testid="backfill-task-duration"
              />
              <Select value={durationUnit} onValueChange={setDurationUnit}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {localDuration && (
              <Badge variant="outline" className="text-blue-600">
                Est: {localDuration} {durationUnit}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Material Form */}
        <div className="lg:col-span-1">
          <Card className="border border-border shadow-card bg-card sticky top-4">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Add Backfill
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">Calculate backfill material quantities. Enter loose CY and the calculator applies compaction factors. Add material and trucking costs per ton.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings className="w-4 h-4 mr-1" />
                      Manage
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Manage Custom Backfill Materials</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {showAddForm ? (
                        <Card className="bg-muted/30">
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Material Name</Label>
                                <Input
                                  value={newMaterial.name}
                                  onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                                  placeholder="e.g., Custom Gravel"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Category</Label>
                                <Input
                                  value={newMaterial.category}
                                  onChange={(e) => setNewMaterial({...newMaterial, category: e.target.value})}
                                  placeholder="Custom"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Tons per CY</Label>
                                <Input
                                  type="number"
                                  value={newMaterial.tonsPerCY}
                                  onChange={(e) => setNewMaterial({...newMaterial, tonsPerCY: e.target.value})}
                                  placeholder="1.4"
                                  className="h-8 text-sm"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Compaction Factor</Label>
                                <Input
                                  type="number"
                                  value={newMaterial.compactionFactor}
                                  onChange={(e) => setNewMaterial({...newMaterial, compactionFactor: e.target.value})}
                                  placeholder="0.90"
                                  className="h-8 text-sm"
                                  min="0.5"
                                  max="1"
                                  step="0.01"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={addCustomMaterial}>
                                <Check className="w-3 h-3 mr-1" />
                                Add Material
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Add New Custom Material
                        </Button>
                      )}

                      {customMaterials.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Your Custom Materials</Label>
                          {customMaterials.map(material => (
                            <div key={material.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                              <span className="flex-1 text-sm">{material.name}</span>
                              <span className="text-xs text-muted-foreground">{material.tonsPerCY} t/CY</span>
                              <span className="text-xs text-muted-foreground">{material.compactionFactor}x</span>
                              <Button size="sm" variant="ghost" onClick={() => removeCustomMaterial(material.id)} className="h-7 px-2">
                                <X className="w-3 h-3 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  Material Type
                </Label>
                <Select value={selectedMaterial} onValueChange={(val) => {
                  setSelectedMaterial(val);
                  setCustomWeight('');
                  setCustomCompaction('');
                }}>
                  <SelectTrigger data-testid="backfill-material-select">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {Object.entries(groupedMaterials).map(([category, mats]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                          {category}
                        </div>
                        {mats.map(material => (
                          <SelectItem key={material.name} value={material.name}>
                            <div className="flex items-center justify-between w-full pr-4">
                              <span className="text-sm">{material.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {material.tonsPerCY} t/CY
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedMaterialData && (
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Category:</span>
                    <span className="font-semibold text-foreground">{selectedMaterialData.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Weight:</span>
                    <span className="font-semibold text-primary">{selectedMaterialData.tonsPerCY} tons/CY</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Compaction:</span>
                    <span className="font-semibold text-accent">{selectedMaterialData.compactionFactor}x</span>
                  </div>
                  {selectedMaterialData.description && (
                    <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                      {selectedMaterialData.description}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    Cubic Yards (Loose)
                  </Label>
                  <Input
                    type="number"
                    placeholder="Enter CY"
                    value={cubicYards}
                    onChange={(e) => setCubicYards(e.target.value)}
                    min="0"
                    step="0.01"
                    data-testid="backfill-cy-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    Compaction Factor
                  </Label>
                  <Input
                    type="number"
                    placeholder={selectedMaterialData?.compactionFactor?.toString() || "0.90"}
                    value={customCompaction}
                    onChange={(e) => setCustomCompaction(e.target.value)}
                    min="0.5"
                    max="1"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    Custom tons/CY
                  </Label>
                  <Input
                    type="number"
                    placeholder={selectedMaterialData?.tonsPerCY?.toString() || "Override"}
                    value={customWeight}
                    onChange={(e) => setCustomWeight(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    Material $/Ton
                  </Label>
                  <Input
                    type="number"
                    placeholder="$0.00"
                    value={materialCost}
                    onChange={(e) => setMaterialCost(e.target.value)}
                    min="0"
                    step="0.01"
                    data-testid="backfill-material-cost"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  Trucking $/Ton
                </Label>
                <Input
                  type="number"
                  placeholder="$0.00"
                  value={truckingCost}
                  onChange={(e) => setTruckingCost(e.target.value)}
                  min="0"
                  step="0.01"
                  data-testid="backfill-trucking-cost"
                />
              </div>

              {selectedMaterialData && cubicYards > 0 && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Compacted Volume:</p>
                      <p className="text-lg font-bold text-accent">
                        {(parseFloat(cubicYards) * (parseFloat(customCompaction) || selectedMaterialData.compactionFactor)).toFixed(2)} CY
                      </p>
                      {customCompaction && <p className="text-xs text-primary">(custom factor)</p>}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Weight:</p>
                      <p className="text-lg font-bold text-success">
                        {(parseFloat(cubicYards) * (parseFloat(customWeight) || selectedMaterialData.tonsPerCY)).toFixed(2)} tons
                      </p>
                      {customWeight && <p className="text-xs text-primary">(custom weight)</p>}
                    </div>
                  </div>
                  {(materialCost > 0 || truckingCost > 0) && (
                    <div className="mt-2 pt-2 border-t border-success/30">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Material:</span>
                          <span className="font-semibold text-foreground ml-2">
                            {formatCurrency((parseFloat(cubicYards) * (parseFloat(customWeight) || selectedMaterialData.tonsPerCY)) * parseFloat(materialCost || 0))}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Trucking:</span>
                          <span className="font-semibold text-foreground ml-2">
                            {formatCurrency((parseFloat(cubicYards) * (parseFloat(customWeight) || selectedMaterialData.tonsPerCY)) * parseFloat(truckingCost || 0))}
                          </span>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-success mt-2">
                        Total: {formatCurrency(
                          (parseFloat(cubicYards) * (parseFloat(customWeight) || selectedMaterialData.tonsPerCY)) * 
                          (parseFloat(materialCost || 0) + parseFloat(truckingCost || 0))
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={addMaterial}
                className="w-full bg-primary hover:bg-primary/90 mt-4"
                size="lg"
                data-testid="add-backfill-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Backfill Material
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Materials List */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Backfill List</h2>
            {backfillItems.length > 0 && (
              <Button
                variant="outline"
                onClick={clearAll}
                size="sm"
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
              >
                Clear All
              </Button>
            )}
          </div>
          
          {backfillItems.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-lg p-8 text-center">
              <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Backfill Added</h3>
              <p className="text-muted-foreground text-sm">
                Select material and enter cubic yards to calculate
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {backfillItems.map(material => (
                <Card key={material.id} className="border-l-4 border-l-teal-500">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{material.name}</p>
                        <p className="text-xs text-muted-foreground">{material.category}</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                          <span className="text-muted-foreground">Loose:</span>
                          <span className="font-medium">{material.looseCubicYards.toFixed(1)} CY</span>
                          <span className="text-muted-foreground">Compacted:</span>
                          <span className="font-medium">{material.compactedCubicYards.toFixed(1)} CY</span>
                          <span className="text-muted-foreground">Weight:</span>
                          <span className="font-medium">{material.totalTons.toFixed(2)} tons</span>
                        </div>
                        {material.totalCost > 0 && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <span className="text-green-600 font-bold">{formatCurrency(material.totalCost)}</span>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMaterial(material.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-teal-500" />
                Backfill Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Items:</span>
                  <span className="font-bold">{grandTotal.itemCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Loose Volume:</span>
                  <span className="font-bold">{grandTotal.totalLooseCY.toFixed(1)} CY</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Compacted Volume:</span>
                  <span className="font-bold text-accent">{grandTotal.totalCompactedCY.toFixed(1)} CY</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Weight:</span>
                  <span className="font-bold">{grandTotal.totalTons.toFixed(2)} tons</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Material Cost:</span>
                    <span className="font-semibold">{formatCurrency(grandTotal.totalMaterialCost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Trucking Cost:</span>
                    <span className="font-semibold">{formatCurrency(grandTotal.totalTruckingCost)}</span>
                  </div>
                  <div className="flex justify-between mt-2 pt-2 border-t">
                    <span className="font-semibold">Total Cost:</span>
                    <span className="font-bold text-lg text-green-600">{formatCurrency(grandTotal.totalCost)}</span>
                  </div>
                </div>
              </div>

              {/* Materials breakdown */}
              {Object.keys(materialTotals).length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-semibold mb-3">By Material:</p>
                  <div className="space-y-2">
                    {Object.values(materialTotals).map(mat => (
                      <div key={mat.name} className="flex justify-between text-sm">
                        <span className="text-muted-foreground truncate flex-1">{mat.name}</span>
                        <span className="font-medium ml-2">{mat.totalTons.toFixed(1)} t</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BackfillCalculator;
